import React from 'react';
import { LegalLayout } from './LegalLayout';

export default function CookiesPage() {
  return (
    <LegalLayout title="Cookie Policy" lastUpdated="9 March 2026">
      <h2>1. Introduction</h2>
      <p>
        This Cookie Policy explains how Bidsentry Ltd, trading as BidSentry ("we", "us", "our"),
        uses cookies and similar technologies when you visit and use the BidSentry platform ("Platform").
        It should be read alongside our <a href="/privacy" className="text-orange-600 hover:text-orange-700">Privacy Policy</a>.
      </p>

      <h2>2. What Are Cookies?</h2>
      <p>
        Cookies are small text files stored on your device when you visit a website. They serve various
        purposes, including remembering your preferences, maintaining your session, and helping us
        understand how the Platform is used.
      </p>

      <h2>3. Cookies We Use</h2>
      <h3>3.1 Essential Cookies (Strictly Necessary)</h3>
      <p>
        These cookies are required for the Platform to function. They enable core features such as
        authentication, session management, and security. Without these cookies, the Platform cannot
        operate correctly.
      </p>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-slate-200 text-left">
            <th className="py-2 pr-4 font-semibold">Cookie</th>
            <th className="py-2 pr-4 font-semibold">Purpose</th>
            <th className="py-2 font-semibold">Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-slate-100">
            <td className="py-2 pr-4 font-mono text-xs">bs_session</td>
            <td className="py-2 pr-4">Maintains your authenticated session</td>
            <td className="py-2">Session / 24 hours</td>
          </tr>
          <tr className="border-b border-slate-100">
            <td className="py-2 pr-4 font-mono text-xs">bs_token</td>
            <td className="py-2 pr-4">Stores your authentication token</td>
            <td className="py-2">7 days</td>
          </tr>
          <tr className="border-b border-slate-100">
            <td className="py-2 pr-4 font-mono text-xs">bs_cookie_consent</td>
            <td className="py-2 pr-4">Records your cookie preference choice</td>
            <td className="py-2">12 months</td>
          </tr>
          <tr className="border-b border-slate-100">
            <td className="py-2 pr-4 font-mono text-xs">bs_beta_banner</td>
            <td className="py-2 pr-4">Records dismissal of the beta service notice</td>
            <td className="py-2">12 months</td>
          </tr>
        </tbody>
      </table>
      <p className="mt-2 text-slate-500">
        Essential cookies do not require consent under UK GDPR as they are strictly necessary
        for the provision of the Service.
      </p>

      <h3>3.2 Analytics Cookies (Optional)</h3>
      <p>
        We may in the future introduce analytics cookies to help us understand how users interact with
        the Platform, identify usability issues, and improve the Service. If analytics cookies are enabled,
        they will only be set with your explicit opt-in consent.
      </p>
      <p>
        <strong>Current status:</strong> No analytics cookies are active. If analytics is introduced,
        you will be presented with a clear opt-in consent toggle before any analytics cookies are set.
      </p>

      <h3>3.3 Third-Party Advertising Cookies</h3>
      <p>
        <strong>We do not use third-party advertising cookies.</strong> BidSentry does not serve ads,
        does not participate in advertising networks, and does not track users for advertising purposes.
        No data is shared with advertising platforms.
      </p>

      <h2>4. Managing Cookies</h2>
      <p>
        You can manage cookies through your browser settings. Most browsers allow you to:
      </p>
      <ul>
        <li>View what cookies are stored on your device.</li>
        <li>Delete individual or all cookies.</li>
        <li>Block cookies from specific or all websites.</li>
        <li>Set preferences for first-party vs. third-party cookies.</li>
      </ul>
      <p>
        Please note that disabling essential cookies will impair the functionality of the Platform,
        and you may not be able to use certain features, including authentication.
      </p>

      <h2>5. Changes to This Policy</h2>
      <p>
        We may update this Cookie Policy from time to time, particularly if we introduce new cookie
        categories (such as analytics). Material changes will be communicated via the Platform.
        The "Last Updated" date at the top of this page reflects the most recent revision.
      </p>

      <h2>6. Contact</h2>
      <p>
        For questions about our use of cookies, contact us at:<br />
        <strong>Email:</strong> privacy@bidsentry.io<br />
        <strong>Company:</strong> Bidsentry Ltd<br />
        <strong>Registered Office:</strong> 124 City Road, London, EC1V 2NX, United Kingdom
      </p>
    </LegalLayout>
  );
}
